import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Clock, AlertTriangle, Check, ChevronDown, X, Lock, Trash2 } from 'lucide-react';

const DUP_COLORS = { amber: '#f59e0b', sky: '#0ea5e9', violet: '#8b5cf6', rose: '#f43f5e', emerald: '#10b981', orange: '#f97316', cyan: '#06b6d4', fuchsia: '#d946ef' };
import { mapLeadLabels, labelChipStyle } from '../utils/labelUtils';
import { PROPERTY_STATUS_OPTIONS } from '../constants/dropdownOptions';

const STATUS_OPTIONS = PROPERTY_STATUS_OPTIONS.filter(o => o.value);

const getPropertyStatusColor = (status) => {
  const s = (status || '').toLowerCase();
  if (s === 'active') return 'bg-green-100 text-green-800';
  if (s === 'inactive') return 'bg-gray-100 text-gray-800';
  if (s === 'not started') return 'bg-slate-100 text-slate-700';
  if (s === 'in progress') return 'bg-sky-100 text-sky-800';
  if (s === 'only listing') return 'bg-violet-100 text-violet-800';
  if (s.includes('terminated') || s.includes('handed')) return 'bg-red-100 text-red-800';
  if (s.includes('owner left')) return 'bg-orange-100 text-orange-800';
  return 'bg-blue-100 text-blue-800';
};

/* Inline label popover for a single row */
const InlineLabelPicker = ({ leadId, selectedIds, allLabels, onChange }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);

  const toggle = (labelId) => {
    const next = selectedIds.includes(labelId)
      ? selectedIds.filter(id => id !== labelId)
      : [...selectedIds, labelId];
    onChange(leadId, next);
  };

  const chips = mapLeadLabels(selectedIds, allLabels);

  return (
    <div ref={ref} className="relative" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setOpen(!open)}
        className="flex items-center gap-1 text-xs text-teal-600 hover:text-teal-800 font-medium"
        data-testid={`inline-label-btn-${leadId}`}
      >
        {chips.length > 0 ? (
          <div className="flex flex-wrap gap-1 max-w-[170px]">
            {chips.slice(0, 2).map(lbl => (
              <span key={lbl.id} className="inline-block px-1.5 py-0.5 text-[10px] font-semibold rounded-full whitespace-nowrap" style={labelChipStyle(lbl.color)} data-testid={`label-chip-${lbl.id}`}>
                {lbl.name}
              </span>
            ))}
            {chips.length > 2 && <span className="text-[10px] text-gray-400">+{chips.length - 2}</span>}
          </div>
        ) : (
          <span className="text-gray-400">+ Label</span>
        )}
        <ChevronDown size={12} className={`transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <div className="absolute z-50 mt-1 left-0 w-44 bg-white border border-gray-200 rounded-xl shadow-lg py-1 max-h-52 overflow-y-auto" data-testid={`label-dropdown-${leadId}`}>
          {allLabels.map(lbl => {
            const active = selectedIds.includes(lbl.id);
            return (
              <button key={lbl.id} onClick={() => toggle(lbl.id)}
                className={`w-full flex items-center gap-2 px-3 py-1.5 text-xs text-left hover:bg-gray-50 ${active ? 'bg-teal-50' : ''}`}
                data-testid={`label-opt-${lbl.id}`}>
                <span className="w-2.5 h-2.5 rounded-full shrink-0" style={{ backgroundColor: lbl.color || '#9ca3af' }} />
                <span className="flex-1 truncate">{lbl.name}</span>
                {active && <Check size={12} className="text-teal-600 shrink-0" />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* Compute verification: Verified when property_status is set AND labels is non-empty */
const isVerified = (lead) => {
  const hasStatus = lead.property_status && lead.property_status.trim() !== '';
  const hasLabels = Array.isArray(lead.labels) && lead.labels.length > 0;
  return hasStatus && hasLabels;
};

const LeadTable = ({ leads, selectedIds, onSelectOne, onSelectAll, onEdit, onDelete, overdueMap = {}, mandatoryPendingMap = {}, showClockColumn = false, allLabels = [], onStatusChange, onLabelsChange, duplicateMap = {}, onApproveDuplicate, onDeleteDuplicate }) => {
  const navigate = useNavigate();
  const allSelected = leads.length > 0 && selectedIds.length === leads.length;

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try { return new Date(dateStr).toLocaleDateString(); } catch { return dateStr; }
  };

  const formatCountdown = (seconds) => {
    if (seconds <= 0) return 'Overdue';
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hrs > 0) return `${hrs}h ${mins}m left`;
    return `${mins}m left`;
  };

  const handleRowClick = (e, lead) => {
    if (e.target.closest('input[type="checkbox"]') || e.target.closest('[data-clock-icon]') || e.target.closest('[data-inline-control]')) return;
    navigate(`/leads/${lead.id}`);
  };

  return (
    <div className="overflow-x-auto border border-gray-200 rounded-lg" style={{ maxWidth: '100%' }}>
      <table className="w-full text-left border-collapse min-w-[1200px]">
        <thead>
          <tr className="bg-gray-50 border-b border-gray-200">
            <th className="px-4 py-3 sticky top-0 bg-gray-50 z-10 w-12">
              <input type="checkbox" className="rounded border-gray-300 text-teal-600 focus:ring-teal-500 w-4 h-4"
                checked={allSelected} onChange={(e) => onSelectAll(e.target.checked)} data-testid="select-all-checkbox" />
            </th>
            {showClockColumn && (
              <th className="px-2 py-3 sticky top-0 bg-gray-50 z-10 w-8"></th>
            )}
            <th className="px-2 py-3 sticky top-0 bg-gray-50 z-10 w-8" title="Lock status"></th>
            <th className="px-4 py-3 sticky top-0 bg-gray-50 z-10 text-xs font-semibold text-gray-500 uppercase tracking-wider">Lead Owner</th>
            <th className="px-4 py-3 sticky top-0 bg-gray-50 z-10 text-xs font-semibold text-gray-500 uppercase tracking-wider">Closed Won Date</th>
            <th className="px-3 py-3 sticky top-0 bg-gray-50 z-10 text-xs font-semibold text-indigo-600 uppercase tracking-wider" data-testid="lead-th-bank-id">Bank ID</th>
            <th className="px-3 py-3 sticky top-0 bg-gray-50 z-10 text-xs font-semibold text-teal-600 uppercase tracking-wider">GRB ID</th>
            <th className="px-4 py-3 sticky top-0 bg-gray-50 z-10 text-xs font-semibold text-gray-500 uppercase tracking-wider">Hotel Name</th>
            <th className="px-4 py-3 sticky top-0 bg-gray-50 z-10 text-xs font-semibold text-gray-500 uppercase tracking-wider">Owner Name</th>
            <th className="px-4 py-3 sticky top-0 bg-gray-50 z-10 text-xs font-semibold text-gray-500 uppercase tracking-wider">Property Status</th>
            <th className="px-4 py-3 sticky top-0 bg-gray-50 z-10 text-xs font-semibold text-gray-500 uppercase tracking-wider">Labels</th>
            <th className="px-4 py-3 sticky top-0 bg-gray-50 z-10 text-xs font-semibold text-gray-500 uppercase tracking-wider">Verification</th>
            <th className="px-4 py-3 sticky top-0 bg-gray-50 z-10 text-xs font-semibold text-gray-500 uppercase tracking-wider">Email Address</th>
            <th className="px-4 py-3 sticky top-0 bg-gray-50 z-10 text-xs font-semibold text-gray-500 uppercase tracking-wider">Mobile Number</th>
            <th className="px-4 py-3 sticky top-0 bg-gray-50 z-10 text-xs font-semibold text-gray-500 uppercase tracking-wider">State</th>
            <th className="px-4 py-3 sticky top-0 bg-gray-50 z-10 text-xs font-semibold text-gray-500 uppercase tracking-wider">City</th>
            <th className="px-4 py-3 sticky top-0 bg-gray-50 z-10 text-xs font-semibold text-gray-500 uppercase tracking-wider">Lead Source</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {leads.map((lead) => {
            const isSelected = selectedIds.includes(lead.id);
            const propertyStatus = lead.property_status || '';
            const overdue = overdueMap[lead.id];
            const verified = isVerified(lead);
            const labelIds = Array.isArray(lead.labels) ? lead.labels : [];
            const dup = duplicateMap[lead.id];
            const dupColor = dup ? (DUP_COLORS[dup.color] || '#f59e0b') : null;

            return (
              <tr key={lead.id} onClick={(e) => handleRowClick(e, lead)}
                style={dup ? { borderLeft: `4px solid ${dupColor}` } : undefined}
                className={`hover:bg-blue-50 transition-colors cursor-pointer ${isSelected ? 'bg-blue-50/50' : ''} ${overdue?.is_overdue ? 'bg-amber-50/30' : ''} ${dup && !dup.approved ? 'bg-rose-50/20' : ''}`}
                data-testid={`lead-row-${lead.id}`}>
                <td className="px-4 py-3">
                  <input type="checkbox" className="rounded border-gray-300 text-teal-600 focus:ring-teal-500 w-4 h-4"
                    checked={isSelected} onChange={(e) => onSelectOne(lead.id, e.target.checked)} />
                </td>
                {showClockColumn && (
                  <td className="px-2 py-3">
                    {mandatoryPendingMap[lead.id] && (
                      <div
                        data-clock-icon="true"
                        data-testid={`clock-icon-${lead.id}`}
                        onClick={(e) => { e.stopPropagation(); navigate(`/leads/${lead.id}#checklist`); }}
                        className="cursor-pointer"
                        title="Mandatory documents pending"
                      >
                        <Clock size={16} className="text-amber-500 animate-pulse" />
                      </div>
                    )}
                  </td>
                )}
                <td className="px-2 py-3">
                  {lead.account_locked && (
                    <span title="Account approved & locked" data-testid={`lock-icon-${lead.id}`}>
                      <Lock size={15} className="text-emerald-600" />
                    </span>
                  )}
                  {dup && (
                    <span title={`Possible duplicate (matched ${dup.match_type})`} className="ml-1 inline-block w-2.5 h-2.5 rounded-full align-middle" style={{ backgroundColor: dupColor }} data-testid={`dup-dot-${lead.id}`} />
                  )}
                </td>
                <td className="px-4 py-3 text-sm text-gray-900">
                  <div className="flex items-center gap-2">
                    <span>{lead.lead_owner || '-'}</span>
                    {dup && !dup.approved && (
                      <span className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
                        <span className="text-[10px] px-1.5 py-0.5 rounded-full font-semibold" style={{ backgroundColor: dupColor + '22', color: dupColor }} data-testid={`dup-badge-${lead.id}`}>Dup</span>
                        <button onClick={() => onApproveDuplicate && onApproveDuplicate(lead)} title="Approve as separate accounts (same owner)" className="text-emerald-600 hover:text-emerald-800" data-testid={`dup-approve-${lead.id}`}><Check size={13} /></button>
                        <button onClick={() => onDeleteDuplicate && onDeleteDuplicate(lead)} title="Delete duplicate account" className="text-rose-500 hover:text-rose-700" data-testid={`dup-delete-${lead.id}`}><Trash2 size={13} /></button>
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">{formatDate(lead.lead_date)}</td>
                <td className="px-3 py-3" data-testid={`bank-id-${lead.id}`}>
                  <span className="inline-block px-2 py-0.5 text-[11px] font-bold tracking-wide rounded bg-indigo-50 text-indigo-700 font-mono whitespace-nowrap">
                    {lead.bank_id || '-'}
                  </span>
                </td>
                <td className="px-3 py-3" data-testid={`grb-id-${lead.id}`}>
                  <span className="inline-block px-2 py-0.5 text-[11px] font-bold tracking-wide rounded bg-teal-50 text-teal-700 font-mono whitespace-nowrap">
                    {lead.grb_property_id || '-'}
                  </span>
                </td>
                <td className="px-4 py-3 text-sm font-medium text-teal-600 hover:text-teal-800 hover:underline">{lead.hotel_name}</td>
                <td className="px-4 py-3 text-sm text-gray-900">{lead.owner_name || '-'}</td>

                {/* Inline Property Status */}
                <td className="px-4 py-3" data-inline-control="true">
                  <select
                    value={propertyStatus}
                    onClick={(e) => e.stopPropagation()}
                    onChange={(e) => { e.stopPropagation(); onStatusChange?.(lead.id, e.target.value); }}
                    className={`px-2 py-1 text-xs font-medium rounded-full border-0 cursor-pointer outline-none appearance-none bg-transparent ${getPropertyStatusColor(propertyStatus || 'Active')}`}
                    style={{ paddingRight: '1.2rem', backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 24 24' fill='none' stroke='currentColor' stroke-width='2'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 4px center' }}
                    data-testid={`inline-status-${lead.id}`}>
                    <option value="">Select</option>
                    {STATUS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </td>

                {/* Inline Labels */}
                <td className="px-4 py-3" data-inline-control="true">
                  <InlineLabelPicker leadId={lead.id} selectedIds={labelIds} allLabels={allLabels} onChange={onLabelsChange} />
                </td>

                {/* Verification */}
                <td className="px-4 py-3" data-testid={`verification-${lead.id}`}>
                  {verified ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-emerald-50 text-emerald-700" data-testid={`verified-badge-${lead.id}`}>
                      <Check size={11} /> Verified
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-xs font-semibold rounded-full bg-amber-50 text-amber-700" data-testid={`unverified-badge-${lead.id}`}>
                      <Clock size={11} /> Unverified
                    </span>
                  )}
                </td>

                <td className="px-4 py-3 text-sm text-gray-500">{lead.email || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{lead.phone || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{lead.state || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{lead.city || '-'}</td>
                <td className="px-4 py-3 text-sm text-gray-500">{lead.source || '-'}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default LeadTable;
